import {BasicEntity, BasicProperty} from '../../basic-entity-back/annotations';
import {Persona} from './persona.model';
import {PropertyType} from '../../basic-entity-back/property-type/property-type';
import {ManagerCachingStrategy, ReadWrite} from '../../basic-entity-back/basic-entity-interface/mapping-external';
import {NumericFilter} from '../../basic-entity-back/filters/numeric-filter';
import {BooleanFilter} from '../../basic-entity-back/filters/boolean-filter';
import {PersonaContacto} from "./contacto.model";
import {NestedBehavior} from "../../basic-entity-back/property-type/nested-model-type";
import {DescuentoCliente} from "../trabajos/descuento-cliente.model";
import {IRI_PROPERTY, TYPE_PROPERTY} from "../../basic-entity-back/basic-entity-interface/mapping-internal";
import {ExactSearchFilter, PartialSearchFilter} from "../../basic-entity-back/filters/search-filter";
import {Comercial} from "./comercial.model";
import {DireccionPersona} from "./direccion-persona.model";
import {
    DireccionesEditionComponent
} from "../../pages/personas/clientes/direcciones-edition/direcciones-edition.component";
import {ClienteAsociado} from "./cliente-asociado.model";
import {FormaPagoCliente} from "../tesoreria/formas-pago/forma-pago-cliente.model";
import {Mandato} from "../tesoreria/mandatos/mandato.model";
import {
    FormasPagoClienteEditionComponent
} from "../../pages/tesoreria/formas-pago-cliente-edition/formas-pago-cliente-edition.component";
import {MandatosEditionComponent} from "../../pages/tesoreria/mandatos-edition/mandatos-edition.component";
import {CuentaBancariaCliente} from "../tesoreria/cuentas-bancarias/cuenta-bancaria-cliente.model";
import {nombreYApellidosCliente} from "./nombre-cliente";
import {DescuentoGeneralClienteRel} from "../documentacion/descuento-general-cliente-rel.model";
import {PrioridadEnvio} from "../envios/prioridad-envio.model";

@BasicEntity({
    name: 'Clientes',
    endpoint: 'clientes',
    isPaginated: true,
    paginationSizes: [20, 30, 50],
    modelNameProperty: 'nombreCompletoCliente',
    acceptsBulkIriSearch: true,
    type: 'Cliente',
    managerCaching: ManagerCachingStrategy.NoCache
})

export class Cliente extends Persona {
    @BasicProperty({
        name: 'Razón Social',
        type: PropertyType.String(),
        sortable: true,
        nullable: true,
        order: 1,
        filters: [PartialSearchFilter]
    })
    private _denominacionSocial;


    // @BasicProperty({
    //     name: 'Código cliente',
    //     type: PropertyType.Integer(),
    //     filters: [NumericFilter],
    //     sortable: true
    // })
    // private _codigo: number = 0;

    @BasicProperty({
        name: 'Baja',
        type: PropertyType.Boolean(),
        filters: [BooleanFilter],
        order: 11
    })
    private _baja = false;

    @BasicProperty({
        name: 'Empresa',
        type: PropertyType.Boolean(),
        filters: [BooleanFilter],
        order: 11
    })
    private _empresa: boolean = false;

    @BasicProperty({
        name: 'Intracomunitaria',
        type: PropertyType.Boolean(),
    })
    private _intracomunitaria: boolean = false;

    @BasicProperty({
        name: 'Contactos',
        type: PropertyType.NestedModel(PersonaContacto, undefined, NestedBehavior.CreateOnly),
        array: true
    })
    private _contactos;

    // De sólo lectura a propósito: la API no acepta escribir asociados anidados en la ficha del
    // cliente, porque la seguridad de operación no cubre las escrituras anidadas. Las altas y
    // ediciones van contra /api/cliente_asociados.
    @BasicProperty({
        name: 'Clientes asociados',
        tag: 'Clientes asociados',
        type: PropertyType.NestedModel(ClienteAsociado),
        array: true,
        readWrite: ReadWrite.ReadOnly
    })
    private _clientesAsociados;

    // Vive en la pestaña de clientes asociados, no en la general: es el permiso que habilita esa
    // pestaña en el panel del cliente, así que se entiende mejor junto a la lista que gobierna.
    @BasicProperty({
        name: 'Puede gestionar clientes asociados',
        tag: 'Clientes asociados',
        type: PropertyType.Boolean()
    })
    private _esPadre: boolean = false;

    @BasicProperty({
        name: 'Descuentos por método de fabricación y material',
        tag: 'Descuentos',
        type: PropertyType.NestedModel(DescuentoCliente, undefined, NestedBehavior.CreateOnly, [IRI_PROPERTY, TYPE_PROPERTY, 'cliente']),
        array: true,
        order: 11
    })
    private _descuentosMetodoFabricacion;

    @BasicProperty({
        name: 'Descuentos genéricos en facturación',
        tag: 'Descuentos',
        type: PropertyType.NestedModel(DescuentoGeneralClienteRel, null, NestedBehavior.CreateOnly),
        array: true,
        order: 20
    })
    private _descuentosGenerales: DescuentoGeneralClienteRel[] = [];

    @BasicProperty({
        name: 'Comercial asociado',
        tag: 'Comercial asociado',
        type: PropertyType.NestedModel(Comercial, 'clientes', NestedBehavior.SendIriOnly),
        nullable: true,
        filters: [ExactSearchFilter],
        order: 10
    })
    private _comercial;

    @BasicProperty({
        name: 'Dirección de facturación',
        tag: 'Direcciones',
        type: PropertyType.NestedModel(DireccionPersona, 'uriPersona', NestedBehavior.CreateOnly, [IRI_PROPERTY, TYPE_PROPERTY, 'personaId', 'tipoDireccion'], DireccionesEditionComponent),
        dontReadFromApi: true,
        nullable: true,
        order: 20
    })
    private _direccionFacturacion;

    // Lista, no una sola: el cliente puede recibir en varios sitios y el desplegable de la línea de
    // pedido ya ofrece todas sus direcciones de envío vigentes. Como la de facturación, es sólo un
    // ancla para el decorador; la lógica vive en el getter/setter 'direccionesEnvio'.
    @BasicProperty({
        name: 'Direcciones de envío',
        tag: 'Direcciones',
        type: PropertyType.NestedModel(DireccionPersona, 'uriPersona', NestedBehavior.CreateOnly, [IRI_PROPERTY, TYPE_PROPERTY, 'personaId', 'tipoDireccion'], DireccionesEditionComponent),
        array: true,
        // La única no se quita: se modifica, o se añade otra antes. La API también lo impide.
        minItems: 1,
        dontReadFromApi: true,
        order: 30
    })
    private _direccionesEnvio;


    @BasicProperty({
        name: 'Formas de pago',
        tag: 'Facturación',
        type: PropertyType.NestedModel(FormaPagoCliente, null, NestedBehavior.CreateOnly, [TYPE_PROPERTY, IRI_PROPERTY, 'cliente', 'fechaCreacion', 'creadoPor', 'modificadoPor', 'version', 'fechaModificacion', 'bloqueadoPor'], FormasPagoClienteEditionComponent),
        array: true,
        nullable: true,
        order: 14
    })
    private _formasPagoDisponibles;


    @BasicProperty({
        name: 'Forma de pago or defecto',
        type: PropertyType.NestedModel(FormaPagoCliente, 'cliente', NestedBehavior.SendIriOnly),
        nullable: true,
        readWrite: ReadWrite.ReadOnly,
        order: 14
    })
    private _formaPagoClientePorDefecto;

    @BasicProperty({
        name: 'Cuentas bancarias',
        tag: 'Facturación',
        type: PropertyType.NestedModel(CuentaBancariaCliente, 'cliente', NestedBehavior.CreateOnly, [TYPE_PROPERTY, IRI_PROPERTY, 'cliente', 'fechaCreacion', 'creadoPor', 'modificadoPor', 'version', 'fechaModificacion', 'bloqueadoPor']),
        array: true,
        nullable: true,
        order: 12
    })
    private _cuentasBancariasCliente;


    @BasicProperty({
        name: 'Mandato',
        tag: 'Facturación',
        type: PropertyType.NestedModel(Mandato, null, NestedBehavior.CreateOnly, [TYPE_PROPERTY, IRI_PROPERTY, 'cliente', 'fechaCreacion', 'creadoPor', 'modificadoPor', 'version', 'fechaModificacion', 'bloqueadoPor'], MandatosEditionComponent),
        nullable: true,
        readWrite: ReadWrite.ReadOnly,
        order: 13
    })
    private _mandato;


    @BasicProperty({
        name: 'Día del mes para realizar el cargo',
        tag: 'Facturación',
        type: PropertyType.Integer(),
        order: 10
    })
    private _diaMesParaCargo = 1;


    @BasicProperty({
        name: 'Quiere factura simplificada',
        tag: 'Facturación',
        type: PropertyType.Boolean(),
        order: 10
    })
    private _facturaSimplificada: boolean = false;

    @BasicProperty({
        name: 'Fecha de alta',
        type: PropertyType.Date(),
        nullable: true,
        order: 1
    })
    private _fechaAlta = null;

    @BasicProperty({
        name: 'Prioridad para envío',
        tag: 'Direcciones',
        type: PropertyType.NestedModel(PrioridadEnvio, null, NestedBehavior.SendIriOnly),
        order: 19
    })
    private _prioridadEnvio: PrioridadEnvio;


    @BasicProperty({
        name: 'Activado',
        type: PropertyType.Boolean(),
        order: 10
    })
    private _activado: boolean = false;

    @BasicProperty({
        name: 'Nombre y apellidos',
        type: PropertyType.String(),
        dontReadFromApi: true,
        readWrite: ReadWrite.ReadOnly
    })
    get nombreCompletoCliente(): string {
        return nombreYApellidosCliente(this);
    }

    get denominacionSocial() {
        return this._denominacionSocial;
    }

    set denominacionSocial(value) {
        this._denominacionSocial = value;
    }

    get descuentosMetodoFabricacion() {
        return this._descuentosMetodoFabricacion;
    }

    set descuentosMetodoFabricacion(value) {
        this._descuentosMetodoFabricacion = value;
    }

    get intracomunitaria(): boolean {
        return this._intracomunitaria;
    }

    set intracomunitaria(value: boolean) {
        this._intracomunitaria = value;
    }


    get empresa(): boolean {
        return this._empresa;
    }

    set empresa(value: boolean) {
        this._empresa = value;
    }

    get contactos() {
        return this._contactos;
    }

    set contactos(value) {
        this._contactos = value;
    }

    get clientesAsociados() {
        return this._clientesAsociados;
    }

    set clientesAsociados(value) {
        this._clientesAsociados = value;
    }

    get esPadre(): boolean {
        return this._esPadre;
    }

    set esPadre(value: boolean) {
        this._esPadre = value;
    }

    get datosCliente(): string {
        let segundaLinea = '';
        if (this.email) {
            segundaLinea = `${this.email}`;
        }
        return segundaLinea;
    }

    get matIcon(): string {
        if (this.empresa) {
            return 'business';
        }
        return 'person';
    }

    get matIconColor(): string {
        if (this.usuario) {
            if (this.usuario.enabled) {
                return 'primary';
            }
            return 'black';
        }
    }

    get comercial() {
        return this._comercial;
    }

    set comercial(value) {
        this._comercial = value;
    }

    /** Las direcciones de envío vigentes. Es lo único que ve y edita la ficha. */
    get direccionesEnvio() {
        return (this.direcciones || []).filter(d => d && this._esEnvioVigente(d));
    }

    /**
     * Sustituye las direcciones de envío vigentes por las del formulario y deja el resto de
     * 'direcciones' -la facturación y todas las históricas- tal cual. Es 'direcciones', no esto, lo
     * que viaja a la API. Quitar una de la lista no la borra: la API la retira (histórica), porque
     * puede estar en pedidos ya emitidos (PersonaSuperclass::setDirecciones()).
     */
    set direccionesEnvio(value) {
        this._direccionesEnvio = value;
        (value || []).forEach(d => d.tipoDireccion = DireccionPersona.TIPOS_DIRECCION.ENVIO);
        this.direcciones = [
            ...(this.direcciones || []).filter(d => d && !this._esEnvioVigente(d)),
            ...(value || [])
        ];
    }

    private _esEnvioVigente(d: DireccionPersona) {
        return d.tipoDireccion === DireccionPersona.TIPOS_DIRECCION.ENVIO && !d.historico;
    }

    get direccionFacturacion() {
        return this.direcciones ? this._direccionVigente(DireccionPersona.TIPOS_DIRECCION.FACTURACION) : null;
    }

    set direccionFacturacion(value) {
        this._direccionFacturacion = value;
        this._asignarDireccion(DireccionPersona.TIPOS_DIRECCION.FACTURACION, value);
    }

    /**
     * Dirección vigente de un tipo.
     *
     * Al modificar una dirección la API conserva la anterior marcada como histórica -los
     * documentos ya emitidos siguen apuntando a ella- y crea una nueva con los datos nuevos.
     * Además la relación llega ordenada con las históricas primero, así que hay que descartarlas
     * explícitamente o la ficha acaba mostrando la antigua.
     */
    private _direccionVigente(tipo: string) {
        return this.direcciones.find(d => d && d.tipoDireccion === tipo && !d.historico);
    }

    /**
     * Vuelca los datos del formulario sobre la dirección vigente de ese tipo, o la da de alta si
     * el cliente todavía no tiene ninguna. Nunca escribe sobre una histórica ni la elimina: son
     * las que mantienen la dirección real de los pedidos y albaranes ya emitidos.
     */
    private _asignarDireccion(tipo: string, value) {
        if (this.direcciones === undefined) {
            this.direcciones = [];
        }

        if (!value) {
            this.direcciones = this.direcciones.filter(d => d.tipoDireccion !== tipo || d.historico);
            return;
        }

        const vigente = this._direccionVigente(tipo);
        if (!vigente) {
            value.tipoDireccion = tipo;
            this.direcciones.push(value);
            return;
        }

        vigente.calle = value.calle;
        vigente.demarcacion = value.demarcacion;
        vigente.poblacion = value.poblacion;
        vigente.pais = value.pais;
        vigente.cp = value.cp;
        vigente.tipoDireccion = tipo;
    }

    get mandato() {
        return this._mandato;
    }

    set mandato(value) {
        this._mandato = value;
    }

    get formasPagoDisponibles() {
        return this._formasPagoDisponibles;
    }

    set formasPagoDisponibles(value) {
        this._formasPagoDisponibles = value;
    }

    get cuentasBancariasCliente() {
        return this._cuentasBancariasCliente;
    }

    set cuentasBancariasCliente(value) {
        this._cuentasBancariasCliente = value;
    }

    get formaPagoClientePorDefecto() {
        return this._formaPagoClientePorDefecto;
    }

    set formaPagoClientePorDefecto(value) {
        this._formaPagoClientePorDefecto = value;
    }

    get baja(): boolean {
        return this._baja;
    }

    set baja(value: boolean) {
        this._baja = value;
    }

    get diaMesParaCargo(): number {
        return this._diaMesParaCargo;
    }

    set diaMesParaCargo(value: number) {
        this._diaMesParaCargo = value;
    }

    get facturaSimplificada(): boolean {
        return this._facturaSimplificada;
    }

    set facturaSimplificada(value: boolean) {
        this._facturaSimplificada = value;
    }

    get fechaAlta(): any {
        return this._fechaAlta;
    }

    set fechaAlta(value: any) {
        this._fechaAlta = value;
    }

    get descuentosGenerales(): DescuentoGeneralClienteRel[] {
        return this._descuentosGenerales;
    }

    set descuentosGenerales(value: DescuentoGeneralClienteRel[]) {
        this._descuentosGenerales = value;
    }

    get prioridadEnvio(): PrioridadEnvio {
        return this._prioridadEnvio;
    }

    set prioridadEnvio(value: PrioridadEnvio) {
        this._prioridadEnvio = value;
    }

    get activado(): boolean {
        return this._activado;
    }

    set activado(value: boolean) {
        this._activado = value;
    }
}

DescuentoCliente.MAP_CLIENTE.type = PropertyType.Uri(Cliente);
Comercial.CLIENTE_MAP.type = PropertyType.Uri(Cliente);
FormaPagoCliente.MAP_CLIENTE.type = PropertyType.Uri(Cliente);
Mandato.MAP_CLIENTE.type = PropertyType.Uri(Cliente);
CuentaBancariaCliente.CLIENTE_MAP.type = PropertyType.Uri(Cliente);
ClienteAsociado.CLIENTE_MAP.type = PropertyType.Uri(Cliente);
